import { debugLog } from "../utils/debugLog";

let vapidPublicKey = null;
let pushSubscription = null;

// Use reminders API URL for local testing, fallback to main API
const REMINDERS_API_URL =
  process.env.REACT_APP_REMINDERS_API_URL || process.env.REACT_APP_API_URL;

/**
 * Get VAPID public key from server
 */
async function getVapidKey() {
  if (vapidPublicKey) return vapidPublicKey;

  try {
    const response = await fetch(
      `${REMINDERS_API_URL}/v2/reminders/push/vapid-key`
    );
    const data = await response.json();

    if (data.status === 200 && data.data?.publicKey) {
      vapidPublicKey = data.data.publicKey;

      return vapidPublicKey;
    }
  } catch (err) {
    console.error("Failed to get VAPID key:", err);
  }

  return null;
}

/**
 * Convert base64 string to Uint8Array for applicationServerKey
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
}

/**
 * Register service worker and get push subscription
 */
export async function initializePush() {
  // Check browser support
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.warn("Push notifications not supported");

    return null;
  }

  try {
    // Get VAPID key from server
    const key = await getVapidKey();

    if (!key) {
      console.warn("VAPID key not available");

      return null;
    }

    // Register service worker
    const registration = await navigator.serviceWorker.register("/push-sw.js", {
      scope: "/",
    });

    await navigator.serviceWorker.ready;

    // Request notification permission
    const permission = await Notification.requestPermission();

    if (permission !== "granted") {
      console.warn("Notification permission denied");

      return null;
    }

    // Get or create push subscription
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key),
      });
    }

    pushSubscription = subscription;
    debugLog("Push subscription:", subscription);

    return subscription.toJSON();
  } catch (err) {
    console.error("Failed to initialize push:", err);

    return null;
  }
}

/**
 * Get current push subscription
 */
export function getPushSubscription() {
  return pushSubscription?.toJSON() || null;
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribePush() {
  if (pushSubscription) {
    try {
      await pushSubscription.unsubscribe();
      pushSubscription = null;

      return true;
    } catch (err) {
      console.error("Failed to unsubscribe:", err);
    }
  }

  return false;
}

const pushService = {
  initializePush,
  getPushSubscription,
  unsubscribePush,
};

export default pushService;
